import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, HandHeart, Home as HomeIcon, Sparkles } from "lucide-react";

const TENANT_THRIVE_LOGO =
  "https://customer-assets.emergentagent.com/job_travel-concierge-hub/artifacts/evbbakhc_Jant%C3%A9%20Tenant%20Thrive%20Logo%20Design.png";

const PILLARS = [
  {
    icon: HandHeart,
    title: "A portion of every dollar",
    body: "Each vacation dollar spent with Janté contributes to Tenant Thrive — no extra cost to you, no footnotes, no small print.",
  },
  {
    icon: HomeIcon,
    title: "Safe, affordable housing",
    body: "Funds are directed toward housing assistance programs in the very destinations we travel — homegrown stability, made possible by guests.",
  },
  {
    icon: Heart,
    title: "Travel that gives back",
    body: "Your journey leaves two impressions — yours, and the community's. Travel with the mission in mind.",
  },
];

export default function TenantThrivePage() {
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-20" data-testid="tenant-thrive-page">
      {/* Hero */}
      <section className="grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
        <div>
          <div className="overline text-[var(--brand-red)]" data-testid="tt-overline">
            Janté · Tenant Thrive
          </div>
          <h1
            className="font-display text-5xl lg:text-6xl text-[var(--emerald)] leading-[1.05] mt-5"
            data-testid="tt-title"
          >
            Vacation with the <em className="text-[var(--brand-red)] not-italic">mission</em> in mind.
          </h1>
          <p
            className="mt-6 text-lg text-[var(--ink-2)] leading-relaxed max-w-xl"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "1.35rem" }}
            data-testid="tt-mission"
          >
            Tenant Thrive redirects a portion of each vacation dollar back into the local
            vacation destinations we travel to — dedicated to providing financial support
            for safe and affordable housing.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/chat" className="btn-primary" data-testid="tt-plan-btn">
              Plan with purpose <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
            <Link to="/join" className="btn-ghost" data-testid="tt-join-btn">
              Join J.T.L
            </Link>
          </div>
        </div>

        <div className="flex justify-center">
          <img
            src={TENANT_THRIVE_LOGO}
            alt="Janté Tenant Thrive"
            className="max-h-[520px] w-auto object-contain"
            data-testid="tt-logo"
          />
        </div>
      </section>

      {/* Pillars */}
      <section className="mt-20 grid md:grid-cols-3 gap-6" data-testid="tt-pillars">
        {PILLARS.map((p) => (
          <div key={p.title} className="lux-card">
            <p.icon size={22} strokeWidth={1.25} className="text-[var(--brand-red)]" />
            <h3 className="font-display text-2xl text-[var(--emerald)] mt-4">{p.title}</h3>
            <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed">{p.body}</p>
          </div>
        ))}
      </section>

      {/* Long-form mission */}
      <section className="mt-20 grid lg:grid-cols-[0.8fr_1.2fr] gap-12 items-start" data-testid="tt-longform">
        <div>
          <div className="overline">Our commitment</div>
          <h2 className="font-display text-4xl text-[var(--emerald)] mt-3 leading-tight">
            Travel with the mission in mind.
          </h2>
        </div>
        <div
          className="space-y-5 text-[var(--ink-2)]"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "1.2rem", lineHeight: 1.6 }}
        >
          <p>
            Janté Travel Concierges exists so extraordinary journeys are possible. Janté
            Tenant Thrive exists so the places that host those journeys — and the people
            who call them home — are cared for in return.
          </p>
          <p>
            Every itinerary Sasha builds, every Host who welcomes you, every driver who
            meets you at the curb, and every meal curated to your table contributes.
            You don't pay more; Janté gives back on your behalf.
          </p>
          <p>
            It is our way of saying thank you — to the streets, the coastlines, and the
            quiet corners of the world that make travel possible.
          </p>
        </div>
      </section>

      {/* CTA block */}
      <section className="mt-20 lux-card-dark text-center" data-testid="tt-cta">
        <Sparkles size={22} strokeWidth={1.25} className="text-[var(--gold)] mx-auto" />
        <h2 className="font-display text-4xl mt-4">Begin your next journey — with meaning.</h2>
        <p className="opacity-85 mt-4 max-w-2xl mx-auto">
          Dream it up with Sasha. Travel it well. Know every mile quietly supports a home
          somewhere in the world.
        </p>
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link to="/chat" className="btn-gold" data-testid="tt-cta-plan">
            Talk to Sasha
          </Link>
          <Link to="/join" className="px-6 py-3 text-[11px] uppercase tracking-[0.2em] border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--emerald)] transition-colors" data-testid="tt-cta-join">
            Join J.T.L
          </Link>
        </div>
      </section>
    </div>
  );
}
